import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, XCircle, Clock, Trash2, RefreshCw, ChevronDown, ChevronUp, Package, Search, Pause, Play } from 'lucide-react';
import authService from '../services/AuthService';
import InfoTooltip from '../components/InfoTooltip';
import MegaExport from '../components/MegaExport';
import { MARKETPLACE_LOGOS, API_BASE } from '../consts';
import DealsViewer from '../components/DealsViewer';
import UpdateDealsOnboardingModal from '../components/UpdateDealsOnboardingModal';

const MaxScansModal = ({ stoppableId, actionLabel, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <AlertCircle className="text-orange-500" size={22} />
                Scan Limit Reached
            </h3>
            <p className="text-gray-600 text-sm mb-1">
                By default, you can run <strong>6 active search</strong> at a time, with up to <strong>15,000 products</strong>. These limits can be increased upon request.
            </p>
            <p className="text-gray-500 text-xs mb-1">
                To proceed, the currently running search must be stopped first.
            </p>
            <p className="text-gray-500 text-xs mb-5">
                To request a limit increase, email us at <a href="mailto:contact@multisellerpro.com" className="text-teal-600 hover:underline font-semibold">contact@multisellerpro.com</a>.
            </p>
            <div className="flex gap-3">
                {stoppableId && (
                    <button
                        onClick={onConfirm}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-semibold transition"
                    >
                        {actionLabel}
                    </button>
                )}
                <button
                    onClick={onCancel}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold transition"
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
);

const CsvColumnPickerModal = ({ file, onConfirm, onCancel }) => {
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [selectedCol, setSelectedCol] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const workbook = XLSX.read(e.target.result, { type: 'binary' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsed = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (parsed.length === 0) return;
            const hdrs = (parsed[0] || []).map(h => String(h));
            setHeaders(hdrs);
            setRows(parsed.slice(1, 6).map(row => hdrs.map((_, i) => String(row[i] ?? ''))));
            const asinIdx = hdrs.findIndex(h => h.toLowerCase().includes('asin'));
            setSelectedCol(asinIdx >= 0 ? asinIdx : 0);
            setLoading(false);
        };
        reader.readAsBinaryString(file);
    }, [file]);

    const handleConfirm = () => {
        // Re-build a new CSV with only the selected column (keep header "asin")
        const reader = new FileReader();
        reader.onload = (e) => {
            const workbook = XLSX.read(e.target.result, { type: 'binary' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const parsed = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const values = parsed.slice(1).map(row => String(row[selectedCol] ?? '').trim()).filter(Boolean);
            const newCsvContent = ['asin', ...values].join('\n');
            const newFile = new File([newCsvContent], file.name.replace(/\.(xlsx|xls)$/i, '.csv'), { type: 'text/csv' });
            onConfirm(newFile);
        };
        reader.readAsBinaryString(file);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999999] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col mt-12">
                {/* Header */}
                <div className="p-6 border-b">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <FileSpreadsheet className="text-teal-600" size={22} />
                        Select the ASIN / Product ID Column
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Your file has multiple columns. Pick which one contains the product IDs.
                    </p>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center py-12">
                        <RefreshCw className="animate-spin text-teal-600" size={32} />
                    </div>
                ) : (
                    <>
                        {/* Column Picker */}
                        <div className="p-6 border-b">
                            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1 border border-gray-100 rounded-lg p-2 bg-gray-50">
                                {headers.map((header, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedCol(idx)}
                                        className={`px-4 py-2 rounded-lg border-2 font-semibold text-sm transition ${selectedCol === idx
                                            ? 'border-teal-600 bg-teal-50 text-teal-700'
                                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                            }`}
                                    >
                                        {header || `Column ${idx + 1}`}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Preview Table */}
                        <div className="flex-1 overflow-auto p-6">
                            <p className="text-xs text-gray-500 mb-3 font-semibold uppercase tracking-wide">
                                Preview (first 5 rows)
                            </p>
                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            {headers.map((h, idx) => (
                                                <th
                                                    key={idx}
                                                    className={`px-4 py-2 text-left font-semibold border-b text-xs max-w-[120px] truncate ${selectedCol === idx
                                                        ? 'text-teal-700 bg-teal-50'
                                                        : 'text-gray-600'
                                                        }`}
                                                >
                                                    {h || `Col ${idx + 1}`}
                                                    {selectedCol === idx && (
                                                        <span className="ml-2 text-xs bg-teal-600 text-white px-1.5 py-0.5 rounded">
                                                            ✓
                                                        </span>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, rIdx) => (
                                            <tr key={rIdx} className="border-b last:border-0">
                                                {headers.map((_, cIdx) => (
                                                    <td
                                                        key={cIdx}
                                                        className={`px-4 py-2 font-mono text-xs max-w-[120px] truncate ${selectedCol === cIdx
                                                            ? 'text-teal-800 bg-teal-50 font-semibold'
                                                            : 'text-gray-500'
                                                            }`}
                                                        title={row[cIdx] || '-'}
                                                    >
                                                        {row[cIdx] || '-'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* Footer */}
                <div className="p-6 border-t flex gap-3">
                    <button
                        onClick={onCancel}
                        className="px-6 py-2 border-2 border-gray-200 rounded-lg text-gray-600 font-semibold hover:bg-gray-50 transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50"
                    >
                        Use "{headers[selectedCol] || `Column ${selectedCol + 1}`}" column
                    </button>
                </div>
            </div>
        </div>
    );
};

const ReverseScanPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('create');
    const [searches, setSearches] = useState([]);
    const [selectedSearch, setSelectedSearch] = useState(null);
    const [loadingSearches, setLoadingSearches] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const currentPageRef = React.useRef(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalSearches, setTotalSearches] = useState(0);
    const [pageSize, setPageSize] = useState(7);
    const pageSizeRef = React.useRef(7);

    useEffect(() => {
        loadSearches();
    }, []);

    const loadSearches = async (silent = false, page = currentPageRef.current, size = pageSizeRef.current) => {
        if (!silent) setLoadingSearches(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/reverse-scan/searches?page=${page}&page_size=${size}`
            );
            const data = await response.json();
            if (response.ok) {
                setSearches(data.searches || []);
                setHasMore(data.has_more || false);
                setTotalSearches(data.total || 0);
            }
        } catch (error) {
            console.error('Failed to load searches:', error);
        } finally {
            if (!silent) setLoadingSearches(false);
        }
    };

    const scrollToDeals = () => {
        const dealsSection = document.getElementById('deals-section');
        if (dealsSection) {
            dealsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Animated background */}
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <Search className="text-teal-600" size={40} />
                        Reverse Scan
                    </h1>
                    <p className="text-gray-600">
                        Find suppliers for marketplace products automatically. Upload ASINs, Keepa searches, or use advanced filters.
                    </p>
                </div>

                <div className="flex justify-end mb-4">
                    <MegaExport scanType="reverse-scan" />
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg mb-6">
                    <div className="flex border-b">
                        <button
                            onClick={() => { setActiveTab('create'); setSelectedSearch(null) }}
                            className={`flex-1 py-4 px-6 font-semibold transition ${activeTab === 'create'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Upload className="inline mr-2" size={20} />
                            Create New Search
                        </button>
                        <button
                            onClick={() => setActiveTab('searches')}
                            className={`flex-1 py-4 px-6 font-semibold transition flex items-center gap-1 justify-center ${activeTab === 'searches'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Package className="inline mr-2" size={20} />
                            My Searches ({loadingSearches ? <RefreshCw className="animate-spin" size={16} /> : totalSearches})
                        </button>
                    </div>

                    <div className="p-6">
                        {activeTab === 'create' ? (
                            <CreateSearchTab onSearchCreated={() => {
                                loadSearches();
                                setActiveTab('searches');
                            }} existingSearches={searches} marketplacesSellOn={user?.marketplaces_sell_on || []} />
                        ) : (
                            <MySearchesTab
                                searches={searches}
                                setSearches={setSearches}
                                loadingSearches={loadingSearches}
                                onSearchSelect={(search) => {
                                    setSelectedSearch(search);
                                    setTimeout(() => scrollToDeals(), 100);
                                }}
                                onSearchDeleted={loadSearches}
                                pageSize={pageSize}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    pageSizeRef.current = size;
                                    setCurrentPage(1);
                                    currentPageRef.current = 1;
                                    loadSearches(false, 1, size);
                                }}
                                onAutoUpdate={() => loadSearches(true)}
                                currentPage={currentPage}
                                hasMore={hasMore}
                                totalSearches={totalSearches}
                                onPageChange={(page) => {
                                    setCurrentPage(page);
                                    currentPageRef.current = page;
                                    loadSearches(false, page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Selected Search Details */}
                {selectedSearch && (
                    <div id="deals-section">
                        <SearchDetails
                            search={selectedSearch}
                            onClose={() => setSelectedSearch(null)}
                            user={user}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

// Create Search Tab Component
const CreateSearchTab = ({ onSearchCreated, existingSearches, marketplacesSellOn = [] }) => {
    const [searchName, setSearchName] = useState('');
    const [marketplace, setMarketplace] = useState('amazon');
    const [uploadMethod, setUploadMethod] = useState('csv'); // 'csv', 'params', 'paste'
    const [file, setFile] = useState(null);
    const [maxScansModal, setMaxScansModal] = useState(null); // { message, stoppableId }
    const [columnPickerFile, setColumnPickerFile] = useState(null);
    const [params, setParams] = useState({
        // Walmart params
        walmartSearchTerm: '',
        walmartPriceMin: '',
        walmartPriceMax: '',
        walmartBadges: '',

        // eBay params
        ebaySearchTerm: '',
        ebayPriceMin: '',
        ebayPriceMax: '',
    });
    const [loading, setLoading] = useState(false);

    const [files, setFiles] = useState([]);

    const processIncomingFiles = (incomingFiles) => {
        const newFiles = Array.from(incomingFiles).filter(f => /\.(csv|xlsx|xls)$/i.test(f.name));
        if (newFiles.length === 0) return;
        if (newFiles.length === 1) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                const workbook = XLSX.read(ev.target.result, { type: 'binary' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                const colCount = (rows[0] || []).length;
                if (colCount > 1) {
                    setColumnPickerFile(newFiles[0]);
                } else {
                    setFiles(prev => [...prev, newFiles[0]]);
                }
            };
            reader.readAsBinaryString(newFiles[0]);
        } else {
            // For multiple files, check each one for multi-column
            newFiles.forEach(f => {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    const workbook = XLSX.read(ev.target.result, { type: 'binary' });
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                    const colCount = (rows[0] || []).length;
                    if (colCount > 1) {
                        setColumnPickerFile(f);
                    } else {
                        setFiles(prev => [...prev, f]);
                    }
                };
                reader.readAsBinaryString(f);
            });
        }
    };

    const handleFileChange = (e) => {
        processIncomingFiles(e.target.files);
    };

    const handleStartAnyway = async () => {
        const { stoppableId } = maxScansModal;
        setMaxScansModal(null);
        if (stoppableId) {
            await authService.authenticatedFetch(
                API_BASE + `/api/reverse-scan/searches/${stoppableId}/stop`,
                { method: 'POST' }
            );
            // Retry the original submission
            handleCreateSearch();
        }
    };

    const handleCreateSearch = async () => {
        if (!searchName.trim()) {
            alert('Please enter a search name');
            return;
        }

        if (existingSearches.some(s => s.search_name.toLowerCase() === searchName.trim().toLowerCase())) {
            alert('A search with this name already exists. Please choose a different name.');
            return;
        }

        if (uploadMethod === 'csv') {
            if (files.length === 0) {
                alert('Please upload at least one file');
                return;
            }
        }

        if (uploadMethod === 'paste') {
            const asinList = (params.pastedAsins || '').split('\n').map(l => l.trim()).filter(Boolean);
            if (asinList.length === 0) {
                alert('Please paste at least one ASIN');
                return;
            }
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('search_name', searchName);
            formData.append('marketplace', marketplace);
            formData.append('upload_method', uploadMethod === 'paste' ? 'csv' : uploadMethod);

            if (uploadMethod === 'paste') {
                const asinList = (params.pastedAsins || '').split('\n').map(l => l.trim()).filter(Boolean);
                const csv = ['asin', ...asinList].join('\n');
                const pasteFile = new File([csv], 'pasted_asins.csv', { type: 'text/csv' });
                formData.append('file', pasteFile);
            }

            if (files.length > 0) {
                // Merge all files into one CSV
                const allLines = await Promise.all(files.map(f => new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                        const workbook = XLSX.read(ev.target.result, { type: 'binary' });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];
                        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                        // Skip header row, grab first column value from each data row
                        const values = rows.slice(1).map(row => String(row[0] || '').trim()).filter(Boolean);
                        resolve(values);
                    };
                    reader.readAsBinaryString(f);
                })));
                const mergedCsv = ['asin', ...allLines.flat()].join('\n');
                const mergedFile = new File([mergedCsv], 'merged.csv', { type: 'text/csv' });
                formData.append('file', mergedFile);
            }

            if (uploadMethod === 'params') {
                formData.append('params', JSON.stringify(params));
            }

            const response = await authService.authenticatedFetch(
                API_BASE + '/api/reverse-scan/searches',
                {
                    method: 'POST',
                    body: formData
                }
            );

            const data = await response.json();

            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    // Find the currently running search to allow stopping it
                    const runningSearch = existingSearches.find(s =>
                        s.status === 'processing' || s.status === 'pending'
                    );
                    setMaxScansModal({
                        message: data.message + '\n\nMax products per search: 15,000. Max concurrent searches: 6.',
                        stoppableId: runningSearch?.id
                    });
                    return;
                }
                throw new Error(data.error || 'Failed to create search');
            }

            setSearchName('');
            setFiles([]);
            setUploadMethod('csv');
            setMarketplace('amazon');
            setParams({
                walmartSearchTerm: '',
                walmartPriceMin: '',
                walmartPriceMax: '',
                walmartBadges: '',
                ebaySearchTerm: '',
                ebayPriceMin: '',
                ebayPriceMax: '',
            });
            onSearchCreated();
        } catch (error) {
            alert(error.message || 'Failed to create search');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Search Name */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Search Name
                </label>
                <input
                    type="text"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder="e.g., High BSR ASINs, Walmart Electronics, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
            </div>

            {/* Marketplace Selection */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Marketplace
                </label>
                <div className="grid grid-cols-3 gap-4">
                    {['amazon', 'walmart', 'ebay'].map(mp => {
                        const sellsOn = marketplacesSellOn.map(m => m.toLowerCase()).includes(mp);
                        return (
                            <div key={mp} className="relative group">
                                <button
                                    onClick={() => sellsOn && setMarketplace(mp)}
                                    disabled={!sellsOn}
                                    className={`w-full p-4 border-2 rounded-lg transition-all ${!sellsOn
                                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                        : marketplace === mp
                                            ? 'border-teal-600 bg-teal-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <img
                                        src={MARKETPLACE_LOGOS[mp]}
                                        alt={mp}
                                        className="w-12 h-12 object-contain mx-auto mb-2"
                                    />
                                    <div className="text-sm font-semibold capitalize">{mp}</div>

                                    {/* Big X overlay */}
                                    {!sellsOn && (
                                        <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                                            <span className="text-red-500 text-5xl font-bold opacity-60">✕</span>
                                        </div>
                                    )}
                                </button>

                                {/* Tooltip */}
                                {!sellsOn && (
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-gray-900 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center">
                                        You haven't selected <span className="capitalize font-semibold">{mp}</span> in your settings.{' '}
                                        <a
                                            href="/settings"
                                            className="underline text-teal-300 pointer-events-auto"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Go to Settings
                                        </a>{' '}
                                        to enable it.
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                    </div>
                                )
                                }
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Upload Method */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Upload Method
                </label>
                <div className="grid grid-cols-2 gap-4">
                    {marketplace === 'amazon' && (
                        <>
                            <button
                                onClick={() => setUploadMethod('csv')}
                                className={`p-4 border-2 rounded-lg transition-all ${uploadMethod === 'csv'
                                    ? 'border-teal-600 bg-teal-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <FileSpreadsheet className="mx-auto mb-2" size={24} />
                                <div className="text-sm font-semibold">Keepa / ASINs CSV</div>
                            </button>
                            <button
                                onClick={() => setUploadMethod('paste')}
                                className={`p-4 border-2 rounded-lg transition-all ${uploadMethod === 'paste'
                                    ? 'border-teal-600 bg-teal-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <Search className="mx-auto mb-2" size={24} />
                                <div className="text-sm font-semibold">Paste ASINs</div>
                            </button>
                        </>
                    )}
                    {marketplace === 'walmart' && (
                        <>
                            <button
                                onClick={() => setUploadMethod('csv')}
                                className={`p-4 border-2 rounded-lg transition-all ${uploadMethod === 'csv'
                                    ? 'border-teal-600 bg-teal-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <FileSpreadsheet className="mx-auto mb-2" size={24} />
                                <div className="text-sm font-semibold">Item IDs CSV</div>
                            </button>
                            <button
                                onClick={() => setUploadMethod('params')}
                                className={`p-4 border-2 rounded-lg transition-all ${uploadMethod === 'params'
                                    ? 'border-teal-600 bg-teal-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <Search className="mx-auto mb-2" size={24} />
                                <div className="text-sm font-semibold">Search Parameters</div>
                            </button>
                        </>
                    )}
                    {marketplace === 'ebay' && (
                        <>
                            <button
                                onClick={() => setUploadMethod('csv')}
                                className={`p-4 border-2 rounded-lg transition-all ${uploadMethod === 'csv'
                                    ? 'border-teal-600 bg-teal-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <FileSpreadsheet className="mx-auto mb-2" size={24} />
                                <div className="text-sm font-semibold">Item IDs CSV</div>
                            </button>
                            <button
                                onClick={() => setUploadMethod('params')}
                                className={`p-4 border-2 rounded-lg transition-all ${uploadMethod === 'params'
                                    ? 'border-teal-600 bg-teal-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <Search className="mx-auto mb-2" size={24} />
                                <div className="text-sm font-semibold">Search Parameters</div>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Upload Content */}
            {uploadMethod === 'csv' && (
                <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-teal-500 transition cursor-pointer"
                    onClick={() => document.getElementById('file-upload').click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-teal-500', 'bg-teal-50'); }}
                    onDragEnter={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-teal-500', 'bg-teal-50'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-teal-500', 'bg-teal-50'); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-teal-500', 'bg-teal-50');
                        processIncomingFiles(e.dataTransfer.files);
                    }}
                >
                    <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                    />
                    <Upload className="mx-auto text-gray-400 mb-2" size={48} />
                    <p className="text-gray-600">Click or drag & drop CSV files <strong>(multiple files supported, as shown in the webinars!!)</strong></p>
                    {files.length > 0 && (
                        <div className="mt-3 space-y-1">
                            {files.map((f, i) => (
                                <div key={i} className="inline-flex items-center gap-2 bg-teal-100 px-3 py-1 rounded-full text-sm text-teal-800 mr-2">
                                    📄 {f.name}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setFiles(prev => prev.filter((_, idx) => idx !== i)); }}
                                        className="hover:text-red-600 font-bold"
                                    >✕</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {uploadMethod === 'paste' && marketplace === 'amazon' && (
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Paste ASINs <span className="text-gray-400 font-normal">(one per line)</span>
                    </label>
                    <textarea
                        rows={10}
                        value={params.pastedAsins || ''}
                        onChange={(e) => setParams({ ...params, pastedAsins: e.target.value })}
                        placeholder={"B08N5WRWNW\nB07XJ8C8F5\nB09G3HRMVB"}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                        {(params.pastedAsins || '').split('\n').filter(l => l.trim()).length} ASINs entered
                    </p>
                </div>
            )}

            {uploadMethod === 'params' && marketplace === 'walmart' && (
                <div className="space-y-4">
                    {/* Info Alert */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                        <div className="flex items-start">
                            <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0" size={20} />
                            <div>
                                <p className="text-sm font-semibold text-yellow-800 mb-1">Only able to scan 10 Walmart search catalog pages</p>
                                <p className="text-sm text-yellow-700">
                                    Remember to use the <strong>price trick</strong> we learned in the webinars!
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Search Term</label>
                        <input
                            type="text"
                            value={params.walmartSearchTerm}
                            onChange={(e) => setParams({ ...params, walmartSearchTerm: e.target.value })}
                            placeholder="e.g., nike shoes (can be brand / category / free text)"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Min Price</label>
                            <input
                                type="number"
                                value={params.walmartPriceMin}
                                onChange={(e) => setParams({ ...params, walmartPriceMin: e.target.value })}
                                placeholder="10"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Max Price</label>
                            <input
                                type="number"
                                value={params.walmartPriceMax}
                                onChange={(e) => setParams({ ...params, walmartPriceMax: e.target.value })}
                                placeholder="100"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Badges</label>
                        <div className="border border-gray-300 rounded-lg p-3 space-y-2">
                            {[
                                { value: 'bought_since_yesterday', label: 'X bought since yesterday' },
                                { value: 'best_seller', label: 'Best seller' },
                                { value: 'popular_pick', label: 'Popular pick' },
                                { value: 'in_carts', label: "In X people's carts" },
                                { value: 'rollback', label: 'Rollback' }
                            ].map(badge => (
                                <label key={badge.value} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                    <input
                                        type="checkbox"
                                        checked={params.walmartBadges.includes(badge.value)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setParams({ ...params, walmartBadges: [...params.walmartBadges, badge.value] });
                                            } else {
                                                setParams({ ...params, walmartBadges: params.walmartBadges.filter(b => b !== badge.value) });
                                            }
                                        }}
                                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                    />
                                    <span className="text-sm text-gray-700">{badge.label}</span>
                                </label>
                            ))}
                        </div>
                        {params.walmartBadges.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {params.walmartBadges.map(badge => {
                                    const badgeLabel = [
                                        { value: 'bought_since_yesterday', label: 'X bought since yesterday' },
                                        { value: 'best_seller', label: 'Best seller' },
                                        { value: 'popular_pick', label: 'Popular pick' },
                                        { value: 'in_carts', label: "In X people's carts" },
                                        { value: 'rollback', label: 'Rollback' }
                                    ].find(b => b.value === badge)?.label;

                                    return (
                                        <span key={badge} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                            {badgeLabel}
                                            <button
                                                onClick={() => setParams({ ...params, walmartBadges: params.walmartBadges.filter(b => b !== badge) })}
                                                className="hover:bg-blue-200 rounded-full"
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {uploadMethod === 'params' && marketplace === 'ebay' && (
                <div className="space-y-4">
                    {/* Info Alert */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                        <div className="flex items-start">
                            <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0" size={20} />
                            <div>
                                <p className="text-sm font-semibold text-yellow-800 mb-1">Only able to scan 10 Ebay search catalog pages</p>
                                <p className="text-sm text-yellow-700">
                                    Remember to use the <strong>price trick</strong> we learned in the webinars!
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Search Term</label>
                        <input
                            type="text"
                            value={params.ebaySearchTerm}
                            onChange={(e) => setParams({ ...params, ebaySearchTerm: e.target.value })}
                            placeholder="e.g., nike shoes (can be brand / category / free text)"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Min Price</label>
                            <input
                                type="number"
                                value={params.ebayPriceMin}
                                onChange={(e) => setParams({ ...params, ebayPriceMin: e.target.value })}
                                placeholder="10"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Max Price</label>
                            <input
                                type="number"
                                value={params.ebayPriceMax}
                                onChange={(e) => setParams({ ...params, ebayPriceMax: e.target.value })}
                                placeholder="100"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Create Button */}
            <button
                onClick={handleCreateSearch}
                disabled={loading}
                className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-4 rounded-lg font-semibold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {loading ? (
                    <span className="flex items-center justify-center gap-2">
                        <RefreshCw className="animate-spin" size={20} />
                        Creating Search...
                    </span>
                ) : (
                    <span className="flex items-center justify-center gap-2">
                        <Search size={20} />
                        Create Search
                    </span>
                )}
            </button>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    <AlertCircle className="inline mr-2" size={16} />
                    <strong>How it works:</strong> Upload your product list or configure search parameters.
                    MSP Reverse Scan will find where you can buy those products from!
                </p>
            </div>

            {maxScansModal && (
                <MaxScansModal
                    stoppableId={maxScansModal.stoppableId}
                    actionLabel="Stop Running & Start Anyway"
                    onConfirm={handleStartAnyway}
                    onCancel={() => setMaxScansModal(null)}
                />
            )}

            {columnPickerFile && (
                <CsvColumnPickerModal
                    file={columnPickerFile}
                    onConfirm={(newFile) => {
                        setFiles(prev => [...prev, newFile]);
                        setColumnPickerFile(null);
                    }}
                    onCancel={() => {
                        setColumnPickerFile(null);
                    }}
                />
            )}
        </div>
    );
};

// My Searches Tab Component
const MySearchesTab = ({ searches, setSearches, loadingSearches, onSearchSelect, onSearchDeleted, onAutoUpdate, currentPage, hasMore, totalSearches, onPageChange, pageSize, onPageSizeChange }) => {
    const [deletingId, setDeletingId] = useState(null);
    const [rerunningId, setRerunningId] = useState(null);
    const [stoppingId, setStoppingId] = useState(null);
    const [resumingId, setResumingId] = useState(null);
    const [expandedSearches, setExpandedSearches] = useState(new Set());
    const [maxScansModal, setMaxScansModal] = useState(null); // { stoppableId }
    const [notes, setNotes] = useState(() => {
        try { return JSON.parse(localStorage.getItem('rs_notes') || '{}'); }
        catch { return {}; }
    });
    const saveNote = (id, text) => {
        setNotes(prev => {
            const next = { ...prev, [id]: text };
            localStorage.setItem('rs_notes', JSON.stringify(next));
            return next;
        });
    };

    useEffect(() => {
        const hasProcessingSearches = searches.some(s => s.status === 'processing' || s.status === 'pending');

        if (!hasProcessingSearches) return;

        const interval = setInterval(() => {
            onAutoUpdate();
        }, 20000);

        return () => clearInterval(interval);
    }, [searches]);

    const handleStop = async (searchId) => {
        if (!window.confirm('Stop this search? You can resume it later.')) {
            return;
        }

        setStoppingId(searchId);
        try {
            await authService.authenticatedFetch(API_BASE + `/api/reverse-scan/searches/${searchId}/stop`, {
                method: 'POST'
            });
            onSearchDeleted(); // Refresh searches
        } catch (error) {
            alert('Failed to stop search');
        } finally {
            setStoppingId(null);
        }
    };

    const handleResume = async (searchId) => {
        setResumingId(searchId);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/reverse-scan/searches/${searchId}/resume`,
                { method: 'POST' }
            );

            const data = await response.json();

            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    const runningSearch = searches.find(s =>
                        s.id !== searchId && (s.status === 'processing' || s.status === 'pending')
                    );
                    setMaxScansModal({ resumeId: searchId, stoppableId: runningSearch?.id });
                    return;
                }
                throw new Error(data.error || 'Failed to resume search');
            }

            onSearchDeleted();
        } catch (error) {
            alert(error.message || 'Failed to resume search');
        } finally {
            setResumingId(null);
        }
    };

    const handleResumeAnyway = async () => {
        const { resumeId, stoppableId } = maxScansModal;
        setMaxScansModal(null);
        if (stoppableId) {
            await authService.authenticatedFetch(
                API_BASE + `/api/reverse-scan/searches/${stoppableId}/stop`,
                { method: 'POST' }
            );
        }
        await handleResume(resumeId);
    };

    const handleDelete = async (searchId) => {
        if (!window.confirm('Delete this search? All associated deals will be deleted.')) {
            return;
        }
        setDeletingId(searchId);
        setSearches(prev => prev.filter(s => s.id !== searchId));
        try {
            await authService.authenticatedFetch(API_BASE + `/api/reverse-scan/searches/${searchId}`, {
                method: 'DELETE'
            });
            onSearchDeleted();
        } catch (error) {
            onSearchDeleted();
            alert('Failed to delete search');
        } finally {
            setDeletingId(null);
        }
    };

    const handleRerun = async (searchId) => {
        if (!window.confirm('Rerun this search? All existing deals will be deleted and it will restart.')) return;
        setRerunningId(searchId);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/reverse-scan/searches/${searchId}/rerun`,
                { method: 'POST' }
            );
            const data = await response.json();
            if (!response.ok) {
                if (data.error === 'MAX_SCANS_REACHED') {
                    const runningSearch = searches.find(s => s.id !== searchId && (s.status === 'processing' || s.status === 'pending'));
                    setMaxScansModal({ resumeId: searchId, stoppableId: runningSearch?.id });
                    return;
                }
                throw new Error(data.error || 'Failed to rerun');
            }
            onSearchDeleted();
        } catch (error) {
            alert(error.message || 'Failed to rerun search');
        } finally {
            setRerunningId(null);
        }
    };

    const toggleMinimize = (searchId) => {
        setExpandedSearches(prev => {
            const newSet = new Set(prev);
            if (newSet.has(searchId)) {
                newSet.delete(searchId);
            } else {
                newSet.add(searchId);
            }
            return newSet;
        });
    };

    if (loadingSearches) {
        return (
            <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={64} />
                <p className="text-gray-600 text-lg font-semibold">Loading searches...</p>
            </div>
        );
    }

    if (searches.length === 0) {
        return (
            <div className="text-center py-12">
                <Package className="mx-auto text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 text-lg">No searches yet</p>
                <p className="text-gray-500 text-sm">Create your first search to get started!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {[...searches].sort((a, b) => {
                const order = { processing: 0, pending: 1, stopped: 2, completed: 3, failed: 4 };
                return (order[a.status] ?? 5) - (order[b.status] ?? 5);
            }).map(search => (
                <SearchCard
                    key={search.id}
                    search={search}
                    onSelect={() => onSearchSelect(search)}
                    onDelete={() => handleDelete(search.id)}
                    onStop={() => handleStop(search.id)}
                    onResume={() => handleResume(search.id)}
                    isDeleting={deletingId === search.id}
                    isStopping={stoppingId === search.id}
                    isResuming={resumingId === search.id}
                    isMinimized={!expandedSearches.has(search.id)}
                    onToggleMinimize={() => toggleMinimize(search.id)}
                    onRerun={() => handleRerun(search.id)}
                    isRerunning={rerunningId === search.id}
                    note={notes[search.id] || ''}
                    onSaveNote={(text) => saveNote(search.id, text)}
                />
            ))}

            <p className="text-xs text-gray-300 text-center" style={{ marginBottom: '-4px' }}>
                Running slow? Email contact@multisellerpro.com and we'll speed things up for you.
            </p>

            {maxScansModal && (
                <MaxScansModal
                    stoppableId={maxScansModal.stoppableId}
                    actionLabel="Stop Running & Resume"
                    onConfirm={handleResumeAnyway}
                    onCancel={() => setMaxScansModal(null)}
                />
            )}

            <div className="flex items-center justify-center gap-2 mt-4">
                <span className="text-sm text-gray-600">Per page:</span>
                <select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-black bg-white"
                >
                    {[5, 7, 10, 20, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
            </div>

            {(currentPage > 1 || hasMore) && (
                <div className="flex items-center justify-center gap-4 mt-4">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                    >
                        ← Previous
                    </button>
                    <span className="text-sm text-black">Page {currentPage}</span>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={!hasMore}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
};

// Search Card Component
const SearchCard = ({ search, onSelect, onDelete, onStop, onResume, isDeleting, isStopping, isResuming, isMinimized, onToggleMinimize, onRerun, isRerunning, note, onSaveNote }) => {
    const [editingNote, setEditingNote] = useState(false);
    const [noteText, setNoteText] = useState(note);
    useEffect(() => { setNoteText(note); }, [note]);

    const getStatusBadge = () => {
        switch (search.status) {
            case 'completed':
                return <span className="flex items-center justify-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full text-sm font-semibold text-center">
                    <CheckCircle size={16} /> Completed
                </span>;
            case 'processing':
                return <span className="flex items-center justify-center gap-1 text-blue-700 bg-blue-100 px-3 py-1 rounded-full text-sm font-semibold text-center">
                    <RefreshCw className="animate-spin" size={16} /> Processing
                </span>;
            case 'pending':
                return <span className="flex items-center justify-center gap-1 text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full text-sm font-semibold text-center">
                    <Clock size={16} /> Pending
                </span>;
            case 'failed':
                return <span className="flex items-center justify-center gap-1 text-red-700 bg-red-100 px-3 py-1 rounded-full text-sm font-semibold text-center">
                    <XCircle size={16} /> Failed
                </span>;
            case 'stopped':
                return <span className="flex items-center justify-center gap-1 text-gray-700 bg-gray-100 px-3 py-1 rounded-full text-sm font-semibold text-center">
                    <XCircle size={16} /> Stopped
                </span>;
            default:
                return null;
        }
    };

    const marketplaceLogo = MARKETPLACE_LOGOS[search.marketplace];

    // Minimized View
    if (isMinimized) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition">
                <div className="p-4 cursor-pointer" onClick={onToggleMinimize}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                            <button className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded">
                                <ChevronDown size={20} />
                            </button>

                            <div className="flex items-center gap-3">
                                {marketplaceLogo && (
                                    <img src={marketplaceLogo} alt={search.marketplace} className="w-8 h-8 object-contain" />
                                )}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                        {search.search_name}
                                        {editingNote ? (
                                            <input
                                                autoFocus
                                                value={noteText}
                                                onChange={(e) => setNoteText(e.target.value)}
                                                onBlur={() => { onSaveNote(noteText); setEditingNote(false); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') { onSaveNote(noteText); setEditingNote(false); } if (e.key === 'Escape') setEditingNote(false); }}
                                                placeholder="Add a note..."
                                                className="text-sm font-normal border border-gray-300 rounded px-2 py-0.5 w-48 focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <span
                                                onClick={(e) => { e.stopPropagation(); setEditingNote(true); }}
                                                className="text-xs font-normal text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-0.5 rounded-full max-w-xs truncate cursor-pointer hover:bg-yellow-100"
                                                title="Click to edit note"
                                            >
                                                {note ? note : '✏️ note...'}
                                            </span>
                                        )}
                                    </h3>
                                    <div className="text-xs text-gray-500">
                                        {new Date(search.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {getStatusBadge()}

                            <div className="text-center">
                                <div className="text-xs text-gray-600">Products</div>
                                <div className="text-lg font-bold text-blue-800">{search.total_products || 0}</div>
                            </div>

                            <div className="text-center">
                                <div className="text-xs text-gray-600">Deals</div>
                                <div className="text-lg font-bold text-green-800">{search.deals_count || 0}</div>
                            </div>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect();
                                }}
                                className="bg-gradient-to-r from-teal-500 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition"
                            >
                                View Deals
                            </button>

                            {/* Stop/Resume Button */}
                            {search.status === 'stopped' ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onResume();
                                    }}
                                    disabled={isResuming}
                                    className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded disabled:opacity-50"
                                    title="Resume"
                                >
                                    {isResuming ? <RefreshCw className="animate-spin" size={20} /> : <Play size={20} />}
                                </button>
                            ) : (search.status === 'pending' || search.status === 'processing') && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onStop();
                                    }}
                                    disabled={isStopping}
                                    className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded disabled:opacity-50"
                                    title="Stop"
                                >
                                    {isStopping ? <RefreshCw className="animate-spin" size={20} /> : <Pause size={20} />}
                                </button>
                            )}

                            <button
                                onClick={(e) => { e.stopPropagation(); onRerun(); }}
                                disabled={isRerunning}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-1 text-sm"
                                title="Delete deals and rerun"
                            >
                                {isRerunning ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                                Rerun
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                disabled={isDeleting}
                                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50"
                            >
                                {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Expanded View
    return (
        <div className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition p-6">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2 flex-1">
                    <button onClick={onToggleMinimize} className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-100 rounded">
                        <ChevronUp size={20} />
                    </button>

                    <div className="flex items-center gap-3">
                        {marketplaceLogo && (
                            <img src={marketplaceLogo} alt={search.marketplace} className="w-10 h-10 object-contain" />
                        )}
                        <div>
                            <h3 className="text-xl font-bold text-gray-800">
                                {search.search_name}
                            </h3>
                            {editingNote ? (
                                <input
                                    autoFocus
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    onBlur={() => { onSaveNote(noteText); setEditingNote(false); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { onSaveNote(noteText); setEditingNote(false); } if (e.key === 'Escape') setEditingNote(false); }}
                                    placeholder="Add a note..."
                                    className="text-sm border border-gray-300 rounded px-2 py-1 w-64 focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                />
                            ) : (
                                <div
                                    onClick={() => setEditingNote(true)}
                                    className="text-sm text-gray-400 hover:text-gray-700 cursor-pointer flex items-center gap-1"
                                    title="Click to add note"
                                >
                                    ✏️ {note ? <span className="text-yellow-700">{note}</span> : 'Add note...'}
                                </div>
                            )}
                            <div className="text-sm text-gray-500">
                                Created: {new Date(search.created_at).toLocaleDateString()}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Stop/Resume Button */}
                    {search.status === 'stopped' ? (
                        <button
                            onClick={onResume}
                            disabled={isResuming}
                            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded disabled:opacity-50"
                            title="Resume"
                        >
                            {isResuming ? <RefreshCw className="animate-spin" size={20} /> : <Play size={20} />}
                        </button>
                    ) : (search.status === 'pending' || search.status === 'processing') && (
                        <button
                            onClick={onStop}
                            disabled={isStopping}
                            className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded disabled:opacity-50"
                            title="Stop"
                        >
                            {isStopping ? <RefreshCw className="animate-spin" size={20} /> : <Pause size={20} />}
                        </button>
                    )}

                    <button
                        onClick={onRerun}
                        disabled={isRerunning}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-1 text-sm"
                        title="Delete deals and rerun"
                    >
                        {isRerunning ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                        Rerun
                    </button>
                    <button
                        onClick={onDelete}
                        disabled={isDeleting}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded disabled:opacity-50"
                    >
                        {isDeleting ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                    </button>
                </div>
            </div>

            {/* Status Panel */}
            <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-5 gap-2">
                    <div className="text-center p-2 bg-blue-50 rounded">
                        <div className="text-xs text-blue-600 font-semibold">Status</div>
                        {getStatusBadge()}
                    </div>
                    <div className="text-center p-2 bg-yellow-50 rounded">
                        <div className="text-xs text-gray-600 font-semibold">Products</div>
                        <div className="text-lg font-bold text-gray-800">{search.total_products || 0}</div>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                        <div className="text-xs text-green-600 font-semibold">Processing</div>
                        <div className="text-lg font-bold text-green-800">
                            {(search.product_status_breakdown?.pending || 0) + (search.product_status_breakdown?.processing || 0)}
                        </div>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                        <div className="text-xs text-green-600 font-semibold">Completed</div>
                        <div className="text-lg font-bold text-green-800">{search.product_status_breakdown?.completed || 0}</div>
                    </div>
                    <div className="text-center p-2 bg-purple-100 rounded border border-purple-800">
                        <div className="text-xs text-purple-700 font-semibold">Deals Found</div>
                        <div className="text-xl font-bold text-purple-700">{search.deals_count || 0}</div>
                    </div>
                </div>
            </div>

            {/* View Deals Button */}
            <button
                onClick={onSelect}
                className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
            >
                <Search size={20} />
                View Deals
            </button>
        </div>
    );
};

// Search Details Component
const SearchDetails = ({ search, onClose, user }) => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tempDeals, setTempDeals] = useState([]);
    const [hasNewDeals, setHasNewDeals] = useState(false);
    const [ignoredNewDeals, setIgnoredNewDeals] = useState(false);
    const [pendingIncoming, setPendingIncoming] = useState([]);

    useEffect(() => {
        setDeals([]);
        setTempDeals([]);
        setHasNewDeals(false);
        loadDeals();

        const interval = setInterval(() => {
            // Check if any deals are still being reverse scanned
            const reverseStatus = search.reverse_scan_status_breakdown || {};
            const hasActiveReverseScan = (
                (reverseStatus.pending || 0) > 0 ||
                (reverseStatus.processing || 0) > 0 ||
                (reverseStatus.searching || 0) > 0
            );

            if (search.status === 'processing' || hasActiveReverseScan) {
                loadDeals(true);
            }
        }, 20000);

        return () => clearInterval(interval);
    }, [search.id, search.status, search.reverse_scan_status_breakdown]);

    const loadDeals = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/reverse-scan/searches/${search.id}/deals`
            );
            const data = await response.json();
            if (response.ok) {
                const incoming = data.deals || [];
                setDeals(prev => {
                    if (prev.length === 0) return incoming;
                    const existingIds = new Set(prev.map(d => d.id));
                    const newOnes = incoming.filter(d => !existingIds.has(d.id));
                    const updated = prev.map(d => {
                        const match = incoming.find(i => i.id === d.id);
                        return match ? match : d;
                    });
                    if (newOnes.length > 0) {
                        setTempDeals(newOnes);
                        setPendingIncoming(incoming);
                        setHasNewDeals(true);
                    }
                    return updated;
                });
            }
        } catch (error) {
            console.error('Failed to load deals:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg"
            >
                ✕
            </button>
            <UpdateDealsOnboardingModal show={hasNewDeals} scanType="reverse" />
            {hasNewDeals && !ignoredNewDeals && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4">
                    <span>🎉 {tempDeals.length} new deal{tempDeals.length > 1 ? 's' : ''} found!</span>
                    <button
                        onClick={() => {
                            const incomingIds = new Set(pendingIncoming.map(d => d.id));
                            setDeals(prev => [
                                ...tempDeals,
                                ...prev.filter(d => incomingIds.has(d.id))
                            ]);
                            setTempDeals([]);
                            setPendingIncoming([]);
                            setHasNewDeals(false);
                        }}
                        className="bg-teal-500 hover:bg-teal-400 px-3 py-1 rounded-full text-sm font-semibold"
                    >
                        Update
                    </button>
                    <button
                        onClick={() => {
                            setIgnoredNewDeals(true);
                            setHasNewDeals(false);
                        }}
                        className="text-gray-400 hover:text-white text-sm"
                    >
                        ✕
                    </button>
                </div>
            )}
            <DealsViewer
                deals={deals}
                loading={loading}
                title={search.search_name}
                showFilters={true}
                pageName={"reverse-scan"}
                user={user}
            />
        </div>
    );
};

export default ReverseScanPage;